import React, {FC} from 'react';
import {AvatarItem, AvatarStack} from './Avatar';
import {ProgressBar} from './Progress';
import {Badge, Button, IconButton} from './Primitives';
import {cx} from './cx';

export interface WishCardProps {
  image: string;
  title: string;
  note?: string;
  merchant?: string;
  price: string;
  pledged?: number;
  goal?: number;
  contributors?: AvatarItem[];
  status?: string;
  mode?: 'pledge' | 'gift' | 'choose';
  onPrimaryAction?: () => void;
  onShare?: () => void;
  className?: string;
}

const actionLabels: Record<
  NonNullable<WishCardProps['mode']>,
  string
> = {
  pledge: 'Pledge together',
  gift: 'Gift this',
  choose: 'Choose for me',
};

export const WishCard: FC<WishCardProps> = ({
  image,
  title,
  note,
  merchant,
  price,
  pledged = 0,
  goal = 0,
  contributors = [],
  status,
  mode = 'pledge',
  onPrimaryAction,
  onShare,
  className,
}) => {
  const hasProgress = goal > 0;
  return (
    <article className={cx('sui-wish-card', className)}>
      <div className="sui-wish-card__media">
        <img alt={title} src={image} />
        {status && <Badge tone="mustard">{status}</Badge>}
        {onShare && (
          <IconButton
            icon="share"
            label={`Share ${title}`}
            onClick={onShare}
            size="sm"
          />
        )}
      </div>
      <div className="sui-wish-card__body">
        {merchant && <p className="sui-wish-card__merchant">{merchant}</p>}
        <div className="sui-wish-card__title-row">
          <h3>{title}</h3>
          <strong>{price}</strong>
        </div>
        {note && <p className="sui-wish-card__note">{note}</p>}
        {hasProgress && (
          <ProgressBar
            label={`${pledged} pledged`}
            max={goal}
            tone="leaf"
            value={pledged}
          />
        )}
        <div className="sui-wish-card__footer">
          {contributors.length > 0 ? (
            <AvatarStack avatars={contributors} max={4} size="xs" />
          ) : (
            <span className="sui-wish-card__quiet">Be the first</span>
          )}
          <Button
            iconEnd="arrow-right"
            onClick={onPrimaryAction}
            size="sm"
            variant={mode === 'pledge' ? 'primary' : 'secondary'}
          >
            {actionLabels[mode]}
          </Button>
        </div>
      </div>
    </article>
  );
};
